import React, { useEffect, useMemo } from "react";
import { Card, Table, Statistic, Tag, Select } from "antd";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Area,
    BarChart,
    Bar,
} from "recharts";
import { useDispatch } from "react-redux";
import { getData } from "../../../actions/apiCall";
import SalaryAnalytics from "./EmployeeStash";

interface SalarySalesData {
    month: string;
    sales: string;   // comes as string
    salary: string;  // comes as string
    profit?: string; // comes as string
}

const formatNumber = (num: any) => {
    if (num === null || num === undefined || num === "") return "0.00";
    const n = typeof num === "number" ? num : Number(num);
    if (Number.isNaN(n)) return String(num);
    const sign = n < 0 ? "-" : "";
    const abs = Math.abs(n);
    if (abs >= 10000000) return `${sign}${(abs / 10000000).toFixed(2)}Cr`;
    if (abs >= 100000) return `${sign}${(abs / 100000).toFixed(2)}L`;
    if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(2)}K`;
    return `${sign}${abs.toFixed(2)}`;
};

const SalaryVsSalesDashboard: React.FC = () => {
    const [data, setData] = React.useState<SalarySalesData[]>([]);
    const [developerData, setDeveloperData] = React.useState<any[]>([]);
    const [selectedFilter, setSelectedFilter] = React.useState<string>(
        new Date().getFullYear() + " - " + (new Date().getFullYear() + 1)
    );
    const dispatch = useDispatch();

    useEffect(() => {
        getStats();
    }, [selectedFilter]);

    const getStats = async () => {
        try {
            const [startDate, endDate] = selectedFilter.split(" - ");
            const resData = await dispatch(
                getData("/analytics/leads", {
                    startYear: parseInt(startDate),
                    endYear: parseInt(endDate),
                })
            );

            if (resData.code === 200) {
                setData(resData.data?.summary || []);
                setDeveloperData(resData.data?.developerWise || []);
            }
        } catch (error) {
            console.error("Error fetching Salary vs Sales data:", error);
        }
    };

    // Normalize API data
    // const enrichedData = useMemo(() => {
    //     if (!data.length) return [];

    //     return data.map((d) => {
    //         const sales = Number(d.sales) || 0;
    //         const salary = Number(d.salary) || 0;
    //         const profit = sales - salary; // recalc to be safe
    //         return {
    //             month: d.month,
    //             sales,
    //             salary,
    //             profit,
    //             salaryPct: sales > 0 ? (salary / sales) * 100 : 0,
    //         };
    //     });
    // }, [data]);


    const enrichedData = useMemo(() => {
        if (!data.length) return [];

        return data.map((d, idx, arr) => {
            const sales = Number(d.sales) || 0;
            const salary = Number(d.salary) || 0;
            const profit = sales - salary;

            // Previous month reference
            const prev = idx > 0 ? arr[idx - 1] : null;
            const prevSales = prev ? Number(prev.sales) || 0 : 0;
            const prevSalary = prev ? Number(prev.salary) || 0 : 0;

            const salesChangeAmt = prev ? sales - prevSales : 0;
            const salesChangePct = prevSales ? (salesChangeAmt / prevSales) * 100 : 0;

            const salaryChangeAmt = prev ? salary - prevSalary : 0;
            const salaryChangePct = prevSalary ? (salaryChangeAmt / prevSalary) * 100 : 0;

            return {
                month: d.month,
                sales,
                salary,
                profit,
                salaryPct: sales > 0 ? (salary / sales) * 100 : 0,
                salesChangeAmt,
                salesChangePct,
                salaryChangeAmt,
                salaryChangePct,
            };
        });
    }, [data]);

    // summary stats
    const totalSales = enrichedData.reduce((sum, d) => sum + d.sales, 0);
    const totalSalary = enrichedData.reduce((sum, d) => sum + d.salary, 0);
    const totalProfit = enrichedData.reduce((sum, d) => sum + d.profit, 0);
    const avgPct = totalSales > 0 ? (totalSalary / totalSales) * 100 : 0;

    const bestMonth = enrichedData.length
        ? enrichedData.reduce((a, b) => (a.profit > b.profit ? a : b))
        : { month: "-", profit: 0, sales: 0, salary: 0 };

    const worstMonth = enrichedData.length
        ? enrichedData.reduce((a, b) => (a.profit < b.profit ? a : b))
        : { month: "-", profit: 0, sales: 0, salary: 0 };

    // const columns = [
    //     { title: "Month", dataIndex: "month", key: "month" },
    //     {
    //         title: "Sales",
    //         dataIndex: "sales",
    //         key: "sales",
    //         render: (v: number) => v.toLocaleString(),
    //     },
    //     {
    //         title: "Salary",
    //         dataIndex: "salary",
    //         key: "salary",
    //         render: (v: number) => v.toLocaleString(),
    //     },
    //     {
    //         title: "Profit/Loss",
    //         dataIndex: "profit",
    //         key: "profit",
    //         render: (v: number) => (
    //             <span style={{ color: v >= 0 ? "green" : "red" }}>{v.toLocaleString()}</span>
    //         ),
    //     },
    //     {
    //         title: "Salary % of Sales",
    //         dataIndex: "salaryPct",
    //         key: "salaryPct",
    //         render: (v: number) => (isFinite(v) && v > 0 ? v.toFixed(1) + "%" : "--"),
    //     },
    // ];

    const columns = [
        { title: "Month", dataIndex: "month", key: "month" },
        {
            title: "Sales",
            dataIndex: "sales",
            key: "sales",
            render: (v: number) => v.toLocaleString(),
        },
        {
            title: "Δ Sales",
            key: "salesChange",
            render: (_: any, row: any) => (
                <span style={{ color: row.salesChangeAmt >= 0 ? "green" : "red" }}>
                    {row.salesChangeAmt >= 0 ? "▲" : "▼"} {formatNumber(row.salesChangeAmt)} ({row.salesChangePct.toFixed(1)}%)
                </span>
            ),
        },
        {
            title: "Salary",
            dataIndex: "salary",
            key: "salary",
            render: (v: number) => v.toLocaleString(),
        },
        {
            title: "Δ Salary",
            key: "salaryChange",
            render: (_: any, row: any) => (
                <span style={{ color: row.salaryChangeAmt >= 0 ? "green" : "red" }}>
                    {row.salaryChangeAmt >= 0 ? "▲" : "▼"} {formatNumber(row.salaryChangeAmt)} ({row.salaryChangePct.toFixed(1)}%)
                </span>
            ),
        },
        {
            title: "Profit/Loss",
            dataIndex: "profit",
            key: "profit",
            render: (v: number) => (
                <span style={{ color: v >= 0 ? "green" : "red" }}>
                    {v.toLocaleString()}
                </span>
            ),
        },
        {
            title: "Salary % of Sales",
            dataIndex: "salaryPct",
            key: "salaryPct",
            render: (v: number) => (isFinite(v) && v > 0 ? v.toFixed(1) + "%" : "--"),
        },
    ];


    // Business IQ suggestions
    const suggestions: string[] = [];
    if (avgPct > 45)
        suggestions.push("⚠️ Salary expenses are high. Consider optimizing workforce cost.");
    if (worstMonth.profit < 0)
        suggestions.push(`❌ ${worstMonth.month} is in loss. Boost sales or reduce salary.`);
    if (bestMonth.sales < 600000)
        suggestions.push("📈 Target monthly sales > 600,000 for stable margins.");
    if (avgPct < 35)
        suggestions.push("✅ Salary efficiency is excellent. Expand sales team to scale revenue.");

    const getYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = currentYear - 5; i <= currentYear + 5; i++) {
            years.push(`${i} - ${i + 1}`);
        }
        return years;
    };

    return (
        <div className="py-2">
            {/* Filter */}
            <div className="flex justify-end mb-4">
                <Select
                    className="w-48"
                    value={selectedFilter}
                    onChange={(val) => setSelectedFilter(val)}
                >
                    {getYearOptions().map((year) => (
                        <Select.Option key={year} value={year}>
                            {year}
                        </Select.Option>
                    ))}
                </Select>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-6 gap-4 mb-4">
                <Card><Statistic title="Total Sales" value={totalSales.toFixed(2)} /></Card>
                <Card><Statistic title="Total Salary" value={totalSalary.toFixed(2)} /></Card>
                <Card><Statistic title="Overall Profit" value={totalProfit.toFixed(2)} /></Card>
                <Card><Statistic title="Avg. Salary % of Sales" value={avgPct} precision={2} suffix="%" /></Card>
                <Card><Statistic title="Best Month (Profit)" value={bestMonth.month} suffix={`₹${bestMonth.profit.toFixed(2).toLocaleString()}`} /></Card>
                <Card><Statistic title="Worst Month (Profit)" value={worstMonth.month} suffix={`₹${worstMonth.profit.toFixed(2).toLocaleString()}`} /></Card>
            </div>

            {/* Line Chart */}
            <Card className="mb-4">
                <h2 className="text-lg font-semibold mb-2">Salary vs Sales</h2>
                <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={enrichedData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={formatNumber} />
                        <Tooltip formatter={(value: number) => formatNumber(value)} />
                        <Legend />
                        <Line type="monotone" dataKey="sales" name="Sales" stroke="#1890ff" strokeWidth={2} />
                        <Line type="monotone" dataKey="salary" name="Salary" stroke="#52c41a" strokeWidth={2} />
                        <Line type="monotone" dataKey="profit" name="Profit" stroke="#fa8c16" strokeWidth={2} />
                        <Area type="monotone" dataKey="profit" name="Profit" stroke="#fa8c16" fill="#ffd591" opacity={0.3} />
                    </LineChart>
                </ResponsiveContainer>
            </Card>

            {/* Bar Chart */}
            <Card className="mb-4">
                <h2 className="text-lg font-semibold mb-2">Profit & Salary % Comparison</h2>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={enrichedData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={formatNumber} />
                        <Tooltip formatter={(value: number) => formatNumber(value)} />
                        <Legend />
                        <Bar dataKey="sales" name="Sales" fill="#1890ff" />
                        <Bar dataKey="salary" name="Salary" fill="#52c41a" />
                        <Bar dataKey="profit" name="Profit" fill="#fa541c" />
                    </BarChart>
                </ResponsiveContainer>
            </Card>

            {/* Suggestions */}
            <Card className="mb-4">
                <h2 className="text-lg font-semibold mb-2">Business IQ Suggestions</h2>
                {suggestions.map((s, i) => (
                    <Tag key={i} color={s.includes("✅") ? "green" : "red"}>{s}</Tag>
                ))}
            </Card>

            <Card>
                <h2 className="text-lg font-semibold mb-2">Detailed Stats</h2>
                <Table
                    rowKey="month"
                    columns={columns}
                    dataSource={[...enrichedData].reverse()}
                    pagination={false}
                    size="middle"
                />
            </Card>

            <div>
                <SalaryAnalytics sampleData={developerData} />
            </div>
        </div>
    );
};

export default SalaryVsSalesDashboard;
